
import { Input , Label } from "@roberttamas/ui";
import { useId } from "react";
import { useTranslation } from "react-i18next";

import { isHexColor } from "@/lib/color-scheme";
import { cn } from "@/lib/utils";

interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Shown muted next to the label (e.g. the CSS variable name). */
  hint?: string;
  /** Optional placeholder shown in the hex input when value is empty. */
  placeholder?: string;
}

/**
 * A single color picker: a native swatch (`<input type="color">`) paired with a
 * hex text input that stay in sync. Invalid hex is flagged but still editable.
 */
export function ColorField({ label, value, onChange, hint, placeholder }: ColorFieldProps) {
  const { t } = useTranslation();
  const id = useId();
  const valid = value === "" || isHexColor(value);
  // The native color input needs a valid 6-digit hex; fall back when invalid.
  const swatch = isHexColor(value) ? value : "#000000";

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        {hint && <code className="font-mono text-[11px] text-muted-foreground">{hint}</code>}
      </div>
      <div className="flex items-center gap-2">
        <input
          aria-label={t("colorField.swatchAria", { label })}
          type="color"
          value={swatch}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-10 shrink-0 cursor-pointer rounded-md border border-input bg-background p-1"
        />
        <Input
          id={id}
          value={value}
          placeholder={placeholder ?? "#000000"}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => onChange(e.target.value)}
          className={cn("font-mono", !valid && "border-destructive focus-visible:ring-destructive")}
        />
      </div>
    </div>
  );
}
